import { memo } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';

import {
  AuthProvider,
  JWTGuard,
  ResetPasswordRoutes,
  RoleGuard,
  useJWT,
} from '@doger/core/modules/auth';
import { UserRole } from '@doger/core/shared/api';
import { environment } from '@doger/core/shared/config';

import { AppAccessDenied } from '../components/access-denied';
import { Layout } from '../components/layout';
import { AuthPage } from './auth';
import { ChatDetailsPage } from './chat-details';
import { HomePage } from './home';
import { MetricsPage } from './metrics';
import { SettingsPage } from './settings';
import { UserDetailsPage, UsersPage } from './users';
import { UserChatsPage } from './users/user-details/pages/user-chats';
import { UserSessionsPage } from './users/user-details/pages/user-sessions';

const router = createBrowserRouter([
  {
    Component: () => (
      <AuthProvider
        signOutUrl='/sign-in'
        signInUrl='/sign-in'
        GOOGLE_CLIENT_ID={environment.google.clientId}
      >
        <Outlet />
      </AuthProvider>
    ),
    children: [
      ...ResetPasswordRoutes,
      {
        path: '/sign-in',
        element: <AuthPage />,
      },
      {
        path: '/access-denied',
        element: <AppAccessDenied />,
      },
      {
        Component: () => (
          <JWTGuard>
            <RoleGuard
              roles={[UserRole.ADMIN]}
              fallback={<Navigate to='/access-denied' replace />}
            >
              <Layout>
                <Outlet />
              </Layout>
            </RoleGuard>
          </JWTGuard>
        ),
        children: [
          {
            path: '/dashboard',
            element: <HomePage />,
          },
          {
            path: '/metrics',
            element: <MetricsPage />,
          },
          {
            path: '/users',
            element: <UsersPage />,
          },
          {
            path: '/me/*',
            Component: () => {
              const jwt = useJWT();

              return <Navigate to={`/users/${jwt!.id}`} replace />;
            },
          },
          {
            path: '/users/:userId/*',
            element: <UserDetailsPage />,
            children: [
              {
                path: 'chats',
                element: <UserChatsPage />,
              },
              {
                path: 'sessions',
                element: <UserSessionsPage />,
              },
              // {
              //   path: '*',
              //   element: <Navigate to='chats' replace />,
              // },
            ],
          },
          {
            path: '/users/:userId/chats/:sessionId',
            element: <ChatDetailsPage />,
          },
          {
            path: '/settings',
            element: <SettingsPage />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to='/users' replace />,
      },
    ],
  },
]);

export const Pages = memo(() => <RouterProvider router={router} />);
